import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { GraphValidationError } from "@/lib/productGraph";

interface ProductBuilderValidationStripProps {
  errors: GraphValidationError[];
  onSelectNode: (id: string) => void;
}

export function ProductBuilderValidationStrip({
  errors,
  onSelectNode,
}: ProductBuilderValidationStripProps): JSX.Element {
  const { t } = useTranslation();

  if (errors.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 text-xs text-emerald-700">
        <CheckCircle2 className="h-4 w-4" />
        <span>{t("enterpriseProductBuilder.validationOk")}</span>
      </div>
    );
  }

  return (
    <div className="border-t bg-amber-50/60">
      <div className="flex items-center gap-2 px-4 py-1.5">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-xs font-semibold text-amber-900">
          {t("enterpriseProductBuilder.validationCount", { count: errors.length })}
        </span>
      </div>
      <ul className="max-h-[120px] divide-y divide-amber-200/60 overflow-y-auto px-2 pb-2">
        {errors.map((err, idx) => (
          <li
            key={`${err.code}-${err.nodeId ?? err.edgeId ?? idx}`}
            className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs"
          >
            <div className="min-w-0 flex-1">
              <Badge variant="outline" className="mr-2 text-[10px] uppercase">
                {err.code}
              </Badge>
              <span className="text-amber-900">
                {String(
                  t(`enterpriseProductBuilder.validation.${err.code}`, {
                    ...(err.params ?? {}),
                    defaultValue: err.message,
                  }),
                )}
              </span>
            </div>
            {err.nodeId && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 shrink-0 text-xs"
                onClick={() => onSelectNode(err.nodeId!)}
              >
                {t("enterpriseProductBuilder.viewNode")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
